// In-app copy of INTEGRATION.md, for a third-party dev who wants the
// encryption without adopting the whole UI. The source repo isn't public,
// so this app is the only place this content is actually reachable.
import { el } from "./dom";
import { renderSnippetTabs } from "./codeSnippets";
import type { TabId } from "../main";

function pageLink(label: string, tab: TabId, onNavigate: (tab: TabId) => void): HTMLElement {
  const btn = el("button", { type: "button", class: "link-btn" }, [label]);
  btn.addEventListener("click", () => onNavigate(tab));
  return btn;
}

export function renderIntegrationPanel(onNavigate: (tab: TabId) => void): HTMLElement {
  const sdkLinkIntro = pageLink("SDK", "sdk", onNavigate);
  const sdkLinkCli = pageLink("SDK", "sdk", onNavigate);
  const sdkLinkNotDrm = pageLink("SDK", "sdk", onNavigate);
  const serviceApiLink = pageLink("Service API", "service-api", onNavigate);

  return el("section", {}, [
    el("p", { class: "eyebrow" }, ["Developer · Integration guide"]),
    el("h2", {}, ["Integrating secure-crypt into another application"]),
    el("p", { class: "lede" }, [
      "For a third-party app that wants to use secure-crypt's encryption -- for example, sharing a file with a recipient so only they can open it -- without becoming secure-crypt's web UI.",
    ]),

    el("div", { class: "card prose" }, [
      el("p", {}, [
        "There are two separate pieces here, kept separate deliberately:",
      ]),
      el("ul", {}, [
        el("li", {}, [
          el("strong", {}, ["The crypto"]),
          " (encrypt/decrypt) -- do this yourself, client-side, using the ",
          sdkLinkIntro,
          " (JS/TS) or the CLI (any language, via subprocess). This is the actual zero-knowledge part: nothing here ever needs to touch a network call.",
        ]),
        el("li", {}, [
          el("strong", {}, ["Storage/relay"]),
          " (optional) -- if you don't want to run your own storage for encrypted containers or public keys, the ",
          serviceApiLink,
          " already exposes two endpoints for exactly that. Nothing new was added for this integration path, and nothing plaintext-adjacent was added to the server's trust boundary.",
        ]),
      ]),
    ]),

    el("p", { class: "section-label" }, ["1. Do the crypto locally"]),
    el("div", { class: "card prose" }, [
      el("p", {}, [
        el("strong", {}, ["From a web app:"]),
        " install the ",
        sdkLinkCli,
        " and call ",
        el("code", {}, ["encryptFileForRecipient"]),
        "/",
        el("code", {}, ["decryptFileForRecipientPrepare"]),
        " (or the password-based/string variants) directly in the browser. See that page for the full walkthrough, including the \"view instead of download\" pattern for read-only-style sharing.",
      ]),
      el("p", {}, [
        el("strong", {}, ["From a backend or script (any language):"]),
        " shell out to the CLI. The \"share\" side needs no interactive input at all -- the shared secret is a random password generated internally and immediately sealed to the recipient's public key, so it's fully scriptable:",
      ]),
      renderSnippetTabs({
        cli: `python -m cli.main share-file \\\n  --in report.pdf \\\n  --pubkey recipient_public.key \\\n  --out report.enc \\\n  --envelope-out report.envelope`,
      }),
      el("p", {}, [
        "This writes two files -- ",
        el("code", {}, ["report.enc"]),
        " (the encrypted container) and ",
        el("code", {}, ["report.envelope"]),
        " (the sealed password, ~100 bytes). ",
        el("strong", {}, ["Both must reach the recipient."]),
        " How you deliver them is up to your application (e.g. your own database + a share link, an email attachment, whatever you already use) -- secure-crypt does not transmit either one for you.",
      ]),
      el("p", {}, [
        "The recipient opens it with their own private key (passphrase prompted interactively by default; ",
        el("code", {}, ["--passphrase-stdin"]),
        " reads it from stdin instead for scripted/automated use -- never pass it as an argument or environment variable, both leak via shell history and the process list):",
      ]),
      renderSnippetTabs({
        cli: `python -m cli.main open-shared-file \\\n  --in report.enc \\\n  --envelope report.envelope \\\n  --privkey my_private.key \\\n  --out report.pdf`,
      }),
    ]),

    el("p", { class: "section-label" }, ["2. Optional: store/relay via secure-crypt's service"]),
    el("div", { class: "card prose" }, [
      el("p", {}, [
        "If you'd rather not run your own storage, the relay service can hold the encrypted container and publish/look up public keys. It never sees plaintext, passwords, private keys, or the envelope from ",
        el("code", {}, ["share-file"]),
        " above -- those never leave your own systems.",
      ]),
      el("table", {}, [
        el("thead", {}, [
          el("tr", {}, [el("th", {}, ["Method"]), el("th", {}, ["Path"]), el("th", {}, ["Auth"]), el("th", {}, ["Purpose"])]),
        ]),
        el("tbody", {}, [
          el("tr", {}, [
            el("td", {}, ["POST"]),
            el("td", {}, [el("code", {}, ["/public-keys/{owner}"])]),
            el("td", {}, ["API key"]),
            el("td", {}, ["publish a public key under an ID you choose"]),
          ]),
          el("tr", {}, [
            el("td", {}, ["GET"]),
            el("td", {}, [el("code", {}, ["/public-keys/{owner}"])]),
            el("td", {}, ["none"]),
            el("td", {}, ["fetch a public key (public data)"]),
          ]),
          el("tr", {}, [
            el("td", {}, ["POST"]),
            el("td", {}, [el("code", {}, ["/blobs"])]),
            el("td", {}, ["API key"]),
            el("td", {}, ["upload the encrypted container"]),
          ]),
          el("tr", {}, [
            el("td", {}, ["GET"]),
            el("td", {}, [el("code", {}, ["/blobs/{id}"])]),
            el("td", {}, ["API key"]),
            el("td", {}, ["download the encrypted container"]),
          ]),
        ]),
      ]),
      el("p", {}, [
        "Get an API key either way: a self-service one with no setup at all (",
        el("code", {}, ["POST /api-tokens"]),
        ", ~1 hour TTL -- good for short-lived integrations, try it on the ",
        pageLink("Service API", "service-api", onNavigate),
        " page), or a permanent one from whoever operates this deployment -- better for long-running automation. Both are sent identically, as ",
        el("code", {}, ["X-Api-Key: <key_id>.<raw_key>"]),
        ", on every request.",
      ]),
      renderSnippetTabs({
        http:
          `# Publish your public key under an ID your app controls (e.g. a user ID):\n` +
          `curl -X POST "$SERVICE_URL/public-keys/alice-42" \\\n  -H "X-Api-Key: $API_KEY" \\\n  -H "Content-Type: application/octet-stream" \\\n  --data-binary @alice_public.key\n\n` +
          `# Anyone (no auth) can then fetch it to encrypt something for alice-42:\n` +
          `curl "$SERVICE_URL/public-keys/alice-42" -o alice_public.key\n\n` +
          `# Upload the encrypted container produced by share-file:\n` +
          `curl -X POST "$SERVICE_URL/blobs" \\\n  -H "X-Api-Key: $API_KEY" \\\n  --data-binary @report.enc\n# -> { "blob_id": "..." }\n\n` +
          `# Recipient (or your app on their behalf) downloads it later:\n` +
          `curl "$SERVICE_URL/blobs/<blob_id>" -H "X-Api-Key: $API_KEY" -o report.enc`,
      }),
      el("p", {}, [
        el("strong", {}, ["What this does not do:"]),
        " store the envelope. ",
        el("code", {}, ["POST /blobs"]),
        " validates that uploaded bytes parse as a secure-crypt container -- an envelope isn't one, so it can't be uploaded there, and that endpoint's job is deliberately not widened to accept arbitrary small blobs just for this. Deliver the envelope through your own application instead (e.g. a column in your own database next to wherever you already track the share).",
      ]),
    ]),

    el("p", { class: "section-label" }, ["What this integration path is not"]),
    el("div", { class: "card prose" }, [
      el("ul", {}, [
        el("li", {}, [
          "Not a way to get plaintext encryption/decryption done ",
          el("em", {}, ["by"]),
          " the service -- the relay never receives plaintext, ever, from any caller. All crypto happens in your own process (via the SDK or CLI) before anything touches the network.",
        ]),
        el("li", {}, [
          "Not real DRM. \"View instead of download\" (see the ",
          sdkLinkNotDrm,
          " page) means the integrating app chooses not to offer a save-as button -- it doesn't prevent a technically capable recipient from extracting the plaintext once it's decrypted in their own browser tab or process.",
        ]),
      ]),
    ]),
  ]);
}
